import type { ImplementationSection } from './implementation-sections';
import { Hammer } from 'lucide-react';
import { cn } from 'helpers/cn';
import { NavLink } from '@/components/NavLink';
import { SECTION_META, SECTION_ORDER } from './implementation-sections';

/**
 * First-run screen for the implementation page.
 *
 * Same standard as SurveysEmptyState and PipelinesEmptyState — but where those end in
 * a create button, nothing here can be created: rows arrive by experiments finishing
 * with a winner. So the job of this screen is to say what will appear, why nothing has,
 * and that an empty queue is a normal state rather than a broken page.
 */

/** The road an experiment travels to reach this page. */
const STEPS = [
  'An experiment runs to its end on this website.',
  'The statistics decide whether a variant beat the control on conversions or on revenue.',
  'Winners land in the queue here, with their code and screenshots, until someone builds the change into the site itself.',
  'Tick a row once it is built — or deploy the variant, and it moves to Deployed on its own.',
];

type ImplementationEmptyStateProps = {
  /** Experiments that finished without a winner — classified, then not listed. */
  excludedCount: number;
};

export const ImplementationEmptyState = ({ excludedCount }: ImplementationEmptyStateProps) => (
  <main className="flex flex-col gap-[14px]">
    <section className="bg-panel border-line rounded-panel border px-4 py-10 sm:px-6 sm:py-12" data-first-run>
      <div className="mx-auto max-w-2xl text-center">
        <Hammer className="text-dim mx-auto size-10" strokeWidth={1.25} />
        <h1 className="text-foreground m-0 mt-4 text-title font-medium tracking-tight">Nothing to implement yet</h1>
        <p className="supporting mx-auto mt-2 max-w-xl">
          A winning variant only changes the site while the experiment serves it. This page is the queue of wins waiting
          to be built into the site permanently: which experiment won, on what goal, and the code and screenshots to
          build from.
        </p>
        {/* Not a failure state: sites can run honest tests for months before one wins. */}
        <p className="supporting mx-auto mt-2 max-w-xl">
          {excludedCount > 0
            ? `${excludedCount} finished ${excludedCount === 1 ? 'experiment is' : 'experiments are'} not listed because no
               variant beat the control, or the test never gathered enough traffic to tell — there is nothing there to
               build. The numbers are on each experiment's statistics tab.`
            : 'Rows appear here on their own once an experiment ends with a variant beating the control.'}
        </p>

        <NavLink
          to="/experiments/library"
          className={cn(
            'bg-cta hover:bg-ctahover text-cta-foreground mt-5 inline-flex h-9 items-center rounded-lg px-4',
            'text-meta font-medium transition-colors duration-150',
          )}
        >
          Go to experiments
        </NavLink>
      </div>
    </section>

    <section className="bg-panel border-line rounded-panel border">
      <div className="border-line flex items-center justify-between border-b px-3 py-2.5">
        <span className="micro-label">The three sections</span>
        <span className="micro-label tracking-normal">{SECTION_ORDER.length}</span>
      </div>
      {/*
        Straight from SECTION_META, which also titles the real sections — so this
        preview and the page it becomes cannot describe the same three lists
        differently.
      */}
      <div className="grid gap-2.5 p-3 sm:grid-cols-3">
        {SECTION_ORDER.map((section: ImplementationSection) => (
          <div key={section} className="border-line bg-panel2 rounded-card border p-3.5">
            <p className="text-foreground m-0 text-body font-medium">{SECTION_META[section].title}</p>
            <p className="supporting m-0 mt-2">{SECTION_META[section].description}</p>
          </div>
        ))}
      </div>
    </section>

    <section className="bg-panel border-line rounded-panel border">
      <div className="border-line border-b px-3 py-2.5">
        <span className="micro-label">How a row gets here</span>
      </div>
      <ol className="m-0 flex list-none flex-col gap-2.5 p-3">
        {STEPS.map((step, index) => (
          <li key={step} className="flex items-start gap-2.5">
            <span
              className={cn(
                'border-line text-dim num flex size-5 shrink-0 items-center justify-center',
                'rounded-full border text-nano',
              )}
              aria-hidden="true"
            >
              {index + 1}
            </span>
            <p className="supporting m-0">{step}</p>
          </li>
        ))}
      </ol>
    </section>
  </main>
);
